/**
 * Deep clones an object using JSON.parse/stringify.
 *
 * IMPORTANT: When to use deep cloning with Redux and React:
 *
 * 1. Redux State Immutability:
 *    - Redux expects state to be immutable
 *    - Mutating state outside reducers breaks time-travel debugging
 *    - Can cause missed updates or hard-to-find bugs
 *
 * 2. When to Deep Clone:
 *    - When passing Redux state to components that will mutate the data
 *    - When using the data in local state (useState, useReducer)
 *    - When passing data to libraries that may mutate it (e.g., React Flow)
 *    - When you need to ensure child components can safely modify the data
 *
 * 3. When NOT to Deep Clone:
 *    - When only reading from the data (no mutations)
 *    - When passing primitive values (strings, numbers, booleans)
 *    - When the data is already immutable
 *
 * 4. Common Pitfalls Without Deep Cloning:
 *    - Accidental mutation of Redux state
 *    - Shared state between components
 *    - Unexpected side effects from library mutations
 *    - Broken Redux dev tools functionality
 *
 * @param obj - The object to deep clone
 * @returns A deep clone of the input object
 */
export function cloneDeep<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}
